<template>
  <div v-if="customer" class="py-6">
    <div class="flex justify-between items-center space-x-8 mb-8 border-b pb-4">
      <div>
        <h4 class="text-lg mr-4 leading-none font-semibold">
          {{ $t('general.forms') }}
        </h4>
      </div>
      <div v-if="!customer.isArchived" class="flex items-center space-x-2 flex-shrink-0">
        <o-tooltip :text="$t('components.customer.profile.customerForms.sendRequest')">
          <o-button variant="primary" flat @click="handleRequestForm()">
            {{ $t('components.customer.profile.customerForms.requestForm') }}
          </o-button>
        </o-tooltip>
        <o-tooltip :text="$t('form.addNew')">
          <o-button variant="primary" flat @click="handleAddForm()">
            {{ $t('form.add') }}
          </o-button>
        </o-tooltip>
      </div>
    </div>
    <o-pager
      :button="$t('general.loadMore')"
      :empty="$t('form.empty')"
      :list="customerForms"
      class="mt-4"
    >
      <template #default="{data}">
        <div v-for="(group, key) in split(data)" :key="key" class="mb-8">
          <p class="text-xs mb-3 tracking-wider opacity-80 uppercase">
            {{ key }}
          </p>
          <div class="flex flex-col divide-y overflow-hidden p-2 md:p-2 items-center h-full dark:bg-gray-800/20 dark:border dark:border-gray-800 shadow-lg rounded-2xl mb-4">
            <customer-form
              v-for="customerForm in group"
              :key="customerForm.idCustomerForm"
              :customerForm="customerForm"
              :customer="customer"
              class="w-full"
            />
          </div>
        </div>
      </template>
    </o-pager>

    <request-action
      ref="requestAction"
      :customer="customer"
      @success="onFormRequestSuccess()"
    />

    <add-action
      ref="addAction"
      :customer="customer"
    />
  </div>
</template>

<script setup lang="ts">
import CustomerForm from '@/components/customer/profile/customerForm.vue'
import RequestAction from '@/components/customerForm/actions/Request.vue'
import AddAction from '@/components/customerForm/actions/Add.vue'

import { OPager } from '@/components/elements'
import { EndlessListPager } from '@/_api/lib/pager'

interface Props {
  customer?: Record<string, any> | null
}

const props = withDefaults(defineProps<Props>(), {
  customer: null,
})

const { $api, $bus, $notification, $i18n, $date } = useNuxtApp()

const customerId = computed(() => props.customer?.idCustomer || props.customer?.id)

const requestAction = ref<any>()
const addAction = ref<any>()

const customerForms = reactive(new EndlessListPager((page: number) => $api.customer.forms(String(customerId.value), { page })))

const createNotification = () => {
  $notification.toast({
    title: $i18n.t('components.customer.profile.customerForms.demo'),
    text: $i18n.t('components.customer.profile.customerForms.anotherLine'),
    variant: 'success',
  })

  $notification.toast({
    title: $i18n.t('components.customer.profile.customerForms.demo'),
    text: $i18n.t('components.customer.profile.customerForms.anotherLine'),
    variant: 'danger',
  })

  $notification.toast({
    title: $i18n.t('components.customer.profile.customerForms.demo'),
    text: $i18n.t('components.customer.profile.customerForms.anotherLine'),
    variant: 'warning',
  })

  $notification.toast({
    title: $i18n.t('components.customer.profile.customerForms.demo'),
    text: $i18n.t('components.customer.profile.customerForms.anotherLine'),
    variant: 'info',
  })

  $notification.toast({
    title: $i18n.t('components.customer.profile.customerForms.demo'),
    text: $i18n.t('components.customer.profile.customerForms.anotherLine'),
  })
}

const handleAddForm = () => {
  addAction.value.open()
}

const handleRequestForm = () => {
  requestAction.value.open()
}

const onFormRequestSuccess = () => {
  customerForms.reload()
}

const split = (data: any[]) => {
  return data.reduce((acc: Record<string, any[]>, cur: any) => {
    const date = ($date as any).format(cur.created * 1000, 'MMMM yyyy')
    acc[date] = [...acc[date] || [], cur]
    return acc
  }, {})
}

// created
const uid = getCurrentInstance()?.uid
$bus.on.all('CustomerForm', uid, (customerForm: any) => {
  const current = props.customer?.idCustomer === customerForm.customer?.idCustomer

  if (current) {
    customerForms.reload()
  }
})

onMounted(() => {
  customerForms.load()
})

onBeforeUnmount(() => {
  $bus.off.all(uid)
})
</script>
